import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';

import useWaitUntil from 'utils/hooks/useWaitUntil';

import { SessionButton } from './AttendeeSessionsList.styles';
import { descendantsPropTypes } from './defaults';

const MILLISECONDS_UNTIL_UNLOCK_SESSION = 2 * 60 * 1000; // 2 minutes

export default function JoinSessionButton({
    startTime,
    colors,
    activeFonts,
    styles,
    meta,
    setContent,
    disableContentEditing,
    to,
    isJoinButtonEnabledForced,
}) {
    // Needed in order to not lose query params like guest-access-hash.
    const { search } = useLocation();
    const [isButtonTimeLocked, setIsButtonTimeLocked] = useState(true);
    const isDisabled = !isJoinButtonEnabledForced && isButtonTimeLocked;

    const triggerButtonUnlock = useCallback(() => setIsButtonTimeLocked(false), []);
    useWaitUntil(triggerButtonUnlock, startTime, { threshold: MILLISECONDS_UNTIL_UNLOCK_SESSION });

    return (
        <SessionButton
            $fontFamily={activeFonts[styles.fontFamily]}
            $fontColor={colors[styles.fontColor]}
            $fontColorHover={colors[styles.fontColorHover]}
            $fontSize={styles.fontSize}
            $backgroundColor={colors[styles.backgroundColor]}
            $backgroundColorHover={colors[styles.backgroundColorHover]}
            $borderColor={colors[styles.borderColor]}
            $borderColorHover={colors[styles.borderColorHover]}
            $borderWidth={styles.borderWidth}
            $borderRadius={styles.borderRadius}
            to={!isDisabled ? `${to}${search}` : search}
            onClick={(e) => {
                // We need to prevent the default action of the link if editing as we want to prevent the user from
                // leaving the page. We also need to prevent the default action so that we can use textEditable.
                if (!disableContentEditing) {
                    e.preventDefault();
                }
            }}
            replace={isJoinButtonEnabledForced}
            disabled={isDisabled}
            tabIndex={isDisabled && '-1'}
            data-qa="session_join_button"
        >
            <TextEditable setContent={setContent} disabled={disableContentEditing}>
                {meta.content}
            </TextEditable>
        </SessionButton>
    );
}

JoinSessionButton.propTypes = {
    startTime: PropTypes.string.isRequired,
    isJoinButtonEnabledForced: PropTypes.bool.isRequired,
    colors: PropTypes.array.isRequired,
    activeFonts: PropTypes.array.isRequired,
    styles: PropTypes.shape(descendantsPropTypes.sessionButton.styles).isRequired,
    meta: PropTypes.shape(descendantsPropTypes.sessionButton.meta).isRequired,
    setContent: PropTypes.func.isRequired,
    disableContentEditing: PropTypes.bool,
    to: PropTypes.string.isRequired,
};

JoinSessionButton.defaultProps = {
    disableContentEditing: false,
};
